import React from 'react';
import Select, { components } from 'react-select';
import InstitutionBadge from '../InstitutionBadge.jsx';
import { describeInstitution } from '../../../utilities/accountIdentity.js';

/**
 * A searchable institution picker, each choice drawn with the institution's badge. It shares
 * AccountSelect's class prefix, so the two look alike and the same test helpers drive both.
 *
 * `value` is an institution's id, or '' for none; `onChange` is given the same. `emptyLabel` offers
 * '' as a choice of its own, under that name (the add-account form's "Imported accounts"); without
 * it, '' shows `placeholder` and cannot be chosen back. `name` is the hidden input's, for a form, and
 * `ariaLabel` names the picker, which sits beside its visible label rather than inside a <label>:
 * a label passes its clicks on to the input, and react-select then opens and shuts its menu at once.
 */
export default function InstitutionSelect({
	institutions,
	value,
	onChange,
	emptyLabel = null,
	placeholder = 'Choose an institution',
	name = 'institution_id',
	ariaLabel = 'Institution',
	className = 'account_edit_institution_select',
}) {
	const options = institutions.map(institutionOption);
	if(emptyLabel !== null) {
		options.unshift({ value: '', label: emptyLabel, badge: { institution: null, name: emptyLabel, color: null } });
	}
	const wanted = value === null || value === undefined ? '' : String(value);

	return (
		<Select
			className={`account_select ${className}`}
			classNamePrefix='account_select'
			options={options}
			value={options.find((option) => option.value === wanted) || null}
			onChange={(option) => onChange(option.value === '' ? '' : Number(option.value))}
			name={name}
			aria-label={ariaLabel}
			placeholder={placeholder}
			components={{ Option: InstitutionOption, SingleValue: InstitutionSingleValue }}
			menuPlacement='auto'
			maxMenuHeight={360}
			/** The picker sits in a modal, which would clip an absolutely positioned menu. */
			menuPortalTarget={typeof document === 'undefined' ? null : document.body}
			styles={STYLES}
		/>
	);
}

const STYLES = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

/** An institution's choice. react-select searches `label`, which is the name the budget gave it. */
export function institutionOption(institution) {
	const { name, color, logo } = describeInstitution(null, institution);
	return {
		value: String(institution.id),
		label: name || `Institution ${institution.id}`,
		badge: { institution: logo, name, color },
	};
}

function InstitutionOption(props) {
	return (
		<components.Option {...props}>
			<InstitutionChoice option={props.data} />
		</components.Option>
	);
}

function InstitutionSingleValue(props) {
	return (
		<components.SingleValue {...props}>
			<InstitutionChoice option={props.data} />
		</components.SingleValue>
	);
}

function InstitutionChoice({ option }) {
	return (
		<span className='institution_select_choice'>
			<InstitutionBadge {...option.badge} size={20} />
			<span className='institution_select_name'>{option.label}</span>
		</span>
	);
}
